import { AppState } from "@Store/index";
import { useEffect, memo, useRef } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import styles from "./searchRegionDesktop.module.scss";
import CommonSectionFeatured from "@Components/CommonSectionFeatured";
import LazyLoadOnScroll from "@Components/LazyLoadOnScroll";
import FooterDesktop from "@Components/FooterV2/FooterDesktop";
import HeaderDesktop from "@Components/Header/HeaderDesktop";
import FeaturedAccomodations from "../components/SectionRegionElements/components/FeaturedAccomodations/FeaturedAccomodations";
import { PAGE } from "constants/index";
import SectionRegionElements from "../components/SectionRegionElements";
// import SearchRegionGrid from "../components/SectionRegionElements/components/SearchRegionGrid/SearchRegionGrid";
import useWhitelabel from "hooks/useWhitelabel";
import loadable from "@loadable/component";
import FullPageLoader from "@Components/FullPageLoader";
import wrapperHOC from "@Utils/wrapperHOC";
import HeaderCustomLinksDesktop from "@Components/HeaderCustomLinks/HeaderCustomLinksDesktop/HeaderCustomLinksDesktop";
import WhiteLabelBreadCrumbs from "@Pages/WhitelabelHome/components/WhiteLabelBreadCrumbs/WhiteLabelBreadCrumbs";
import BreadCrumbs from "@Components/BreadCrumbs";
import { getRegionBreadcrumb } from "../components/SearchHeadingContent/constant";
import useTranslationHook from "hooks/useTranslationHook";
import { Helmet } from "react-helmet";
import { getSearchMetaTitle } from "../data/helper";
import Insights from "@Pages/Home/components/Insights";
import CitiesSection from "../components/CitiesSection/index";
import AreasSection from "../components/AreasSection/index";
import UniversitiesSection from "../components/UniversitiesSection/index";
import SearchLeadFormSection from "../components/SearchLeadFormSection/SearchLeadFormSection";
import { resetLeadSubmitted } from "@Components/LeadFormModal/data/leadFormReducer";
import { CANNIBALIZATION_ROLLOUT } from "constants/configConstants";
import SearchFeaturedBlogs from "../components/SearchFeaturedBlogs/SearchFeaturedBlogs";

const FooterDesktopWhiteLabel = loadable(
  () => import("@Components/FooterWhiteLabel/FooterDesktopWhiteLabel"),
  {
    fallback: <FullPageLoader height="284px" />,
  },
);

const SearchRegionDesktop = () => {
  const { region, isCountry, isLeadFormFilled, isLeadSubmitted } = useSelector(
    ({ searchPageData, leadFormModal }: AppState) => ({
      region: searchPageData.region,
      isCountry: searchPageData.regionList.isCountry,
      isLeadFormFilled: leadFormModal.isLeadFormFilled,
      isLeadSubmitted: leadFormModal.isLeadSubmitted,
    }),
    shallowEqual,
  );

  const dispatch = useDispatch();
  const isInitialFilled = useRef(isLeadFormFilled === "true");

  useEffect(
    () => () => {
      dispatch(resetLeadSubmitted());
    },
    [dispatch],
  );

  const { isWhitelabel, whitelabelConfig } = useWhitelabel();
  const { t } = useTranslationHook();

  const pageTitle = isWhitelabel && region ? getSearchMetaTitle({}, { region }) : null;
  const isUSA =
    region?.location?.country?.long_name === "United States" ||
    region?.location?.country?.long_name === "Canada";

  // cannibalization check: Hide breadcrumbs for country/state pages on global
  const isAU = CANNIBALIZATION_ROLLOUT.includes(region?.location?.country?.short_name || "");
  const isCountryOrStatePage = ["country", "state"].includes(region?.region_type);
  const shouldHideBreadcrumbsForCountryState = isCountryOrStatePage && !isAU;

  return (
    <>
      {isWhitelabel && pageTitle && <Helmet title={pageTitle} />}
      <div className={styles.stickeyWrap}>
        <HeaderDesktop parentPage={PAGE.SEARCH} />
      </div>
      {isWhitelabel && whitelabelConfig?.is_custom_link_enabled && (
        <div className={styles.whitelabelCustomLinkContainer}>
          <HeaderCustomLinksDesktop />
        </div>
      )}
      {isWhitelabel && (
        <div className={styles.breadcrumbsContainerWhitelabel}>
          <WhiteLabelBreadCrumbs
            pageType={isCountry ? "country" : "search"}
            regionName={region?.name}
            regionSlug={region?.canonical_name}
          />
        </div>
      )}
      {!isWhitelabel && region && !shouldHideBreadcrumbsForCountryState && (
        <div className={styles.breadcrumbsContainer}>
          <BreadCrumbs
            data={getRegionBreadcrumb(
              region,
              { parent_1: true },
              region?.location?.country?.short_name,
            )}
            isPropertyPage={false}
          />
        </div>
      )}

      <div
        className={`${styles.heroSection} ${
          !isWhitelabel && region && !shouldHideBreadcrumbsForCountryState
            ? styles.withBreadcrumbs
            : ""
        }`}
      >
        <CommonSectionFeatured
          bgUrl="https://prod-static-assets.amberstudent.com/images/search-region-hero-web.jpg?fm=pjpg&auto=format"
          bgSize="cover"
          title={`${t("searchpage.searchregion.herotitlehousing")} ${
            isUSA
              ? t("searchpage.searchregion.housing")
              : t("searchpage.searchregion.accommodation")
          } ${t("searchpage.searchregion.herotitlein")} ${region?.name}`}
          subheading={
            region && region?.name
              ? `<span>${
                  isUSA
                    ? t("searchpage.searchregion.housing")
                    : t("searchpage.searchregion.accommodation")
                } ${t("searchpage.searchregion.herosubheadinghousing")} ${
                  isUSA
                    ? t("searchpage.searchregion.housing")
                    : t("searchpage.searchregion.accommodation")
                } ${t("searchpage.searchregion.herosubheadinghousingforyou")}</span>`
              : `<span>${t("searchpage.searchregion.herosubheadingfallbackyour")} <b> ${t(
                  "searchpage.searchregion.herosubheadingfallbackstudenthousing",
                )}</b> ${t("searchpage.searchregion.herosubheadingfallbackendshere")}</span>`
          }
          isDesktop
        />
      </div>

      <div className={styles.insightsWrapper}>
        <Insights className={styles.isSearchRegionPage} isDesktop fromBaidu={false} />
      </div>

      <div className={styles.wrapper}>
        <div className={styles.rootContainer}>
          <div className={styles.insightsSection}>
            <SectionRegionElements isDesktop />
            {!isWhitelabel && (isCountry || region?.region_type === "state") && <CitiesSection />}
            {!isWhitelabel && (isCountry || region?.region_type === "state") && <AreasSection />}
            {!isWhitelabel && (isCountry || region?.region_type === "state") && (
              <UniversitiesSection />
            )}
            <LazyLoadOnScroll fallback={<section style={{ height: "476px" }} />}>
              <FeaturedAccomodations isDesktop />
            </LazyLoadOnScroll>
            {(!isInitialFilled.current || isLeadSubmitted) && (
              <LazyLoadOnScroll fallback={<section style={{ height: "476px" }} />}>
                <SearchLeadFormSection />
              </LazyLoadOnScroll>
            )}
            <SearchFeaturedBlogs />
          </div>
          {/* <SearchRegionGrid isDesktop /> */}
        </div>
      </div>
      {isWhitelabel ? <FooterDesktopWhiteLabel /> : <FooterDesktop />}
    </>
  );
};

export default wrapperHOC(memo(SearchRegionDesktop), {
  componentName: "SearchRegionDesktop",
  showForChina: true,
});
